import React, { useState } from "react";
import { Row, Col, Image, Input, Empty } from "antd";
import { map, filter } from "lodash";
import styled from "styled-components";
import { PRODUCT_400_400, PRODUCT_MAIN } from "../config/Constant";
import ProductCollection from "./ProductCollection";
import { ProductPageLang } from "../lang/ProductPageLang";
import MainImgBlock from "../pages/MainImgBlock";
import { motion } from "framer-motion";

// Animations
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const zoomIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

// Styled Components
export const PageContainer = styled.div`
  position: relative;
  padding: 60px 0px;
  background: #f9f9f9;
  overflow: hidden;
`;

export const SectionTitle = styled.h2`
  text-align: center;
  font-size: 40px;
  font-weight: 900;
  margin: 40px 0 30px 0;
  background: linear-gradient(90deg, #ff8800, #ffcc00);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 2px;
  text-transform: uppercase;
  position: relative;

  &:after {
    content: "";
    display: block;
    height: 4px;
    width: 80px;
    background: #ff8800;
    margin: 12px auto 0;
    border-radius: 2px;
    transition: width 0.3s ease;
  }

  &:hover:after {
    width: 140px;
  }

  @media (max-width: 1024px) {
    font-size: 32px;
  }
  @media (max-width: 768px) {
    font-size: 28px;
  }
  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

export const SearchBarWrapper = styled.div`
  max-width: 500px;
  margin: 0 auto 40px auto;
  padding: 0 16px;

  .ant-input {
    border-radius: 50px;
    padding: 14px 20px;
    font-size: 1rem;
    border: 1px solid #ddd;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &:focus {
      border-color: #ff8800;
      box-shadow: 0 0 10px rgba(255, 136, 0, 0.4);
    }
  }
`;

export const StyledRow = styled(Row)`
  justify-content: center;
`;

export const StyledColumn = styled(motion(Col))`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #fff;
  padding: 24px;
  margin: 10px;
  border-radius: 16px;
  box-shadow: 0px 6px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;

  &:hover {
    transform: translateY(-6px) scale(1.05);
    box-shadow: 0px 10px 30px rgba(0, 0, 0, 0.15);
  }

  img {
    object-fit: cover;
    background: #eaeaea;
    border-radius: 10px;
    transition: transform 0.4s ease, filter 0.4s ease;

    &:hover {
      transform: scale(1.08);
      filter: brightness(1.1);
    }
  }
`;

export const StyledTitle = styled.div`
  text-align: center;
  color: #222;
  font-weight: 700;
  font-size: 1.2rem;
  margin: 16px 0 0 0;
  letter-spacing: 1px;
  transition: color 0.3s ease;

  &:hover {
    color: #ff8800;
    text-shadow: 0px 0px 8px rgba(255, 136, 0, 0.5);
  }
`;

const Product400x400Page = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredProducts = filter(PRODUCT_400_400, (collection) =>
    collection.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <MainImgBlock title={ProductPageLang.PRODUCT_1} imgLink={PRODUCT_MAIN} />
      <PageContainer>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          variants={fadeUp}
        >
          <SectionTitle>{ProductPageLang.PRODUCT_1}</SectionTitle>
        </motion.div>
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, amount: 0.2 }}
          variants={fadeUp}
        >
          <SearchBarWrapper>
            <Input
              placeholder="🔍 Search by Design Number (e.g. 5006)"
              allowClear
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="large"
            />
          </SearchBarWrapper>
        </motion.div>

        {/* Products Grid */}
        {filteredProducts.length > 0 ? (
          <StyledRow gutter={[24, 24]}>
            {map(filteredProducts, (collection, index) => (
              <StyledColumn
                key={index}
                xs={24}
                sm={12}
                md={8}
                lg={6}
                xl={5}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: false, amount: 0.2 }}
                variants={zoomIn}
              >
                <Image
                  src={collection.image}
                  alt={collection.title}
                  height={250}
                  width={250}
                />
                <StyledTitle>{collection.title}</StyledTitle>
              </StyledColumn>
            ))}
          </StyledRow>
        ) : (
          <Empty description="No designs found" style={{ marginTop: "40px" }} />
        )}
      </PageContainer>

      <ProductCollection />
    </>
  );
};

export default Product400x400Page;
